import { useEffect, useState, useRef } from 'react';
import DOMPurify from 'dompurify';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import RichTextEditor from '../components/RichTextEditor';
import ProductionThread from '../components/ProductionThread';
import {
  Plus, FileText, Upload, Loader2, X, Clock, CheckCircle,
  XCircle, AlertTriangle, PenLine, Eye, Trash2, Edit3,
  BookMarked,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
  pending: { label: 'بانتظار المراجعة', icon: Clock, color: 'text-amber-600 bg-amber-50 border-amber-200' },
  approved: { label: 'مقبول', icon: CheckCircle, color: 'text-green-600 bg-green-50 border-green-200' },
  rejected: { label: 'مرفوض', icon: XCircle, color: 'text-red-600 bg-red-50 border-red-200' },
  changes_requested: { label: 'مطلوب تعديلات', icon: AlertTriangle, color: 'text-orange-600 bg-orange-50 border-orange-200' },
};

const EDITABLE = ['pending', 'changes_requested'];

const MyProductionsPage = () => {
  const [productions, setProductions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProd, setEditingProd] = useState(null);
  const [viewProd, setViewProd] = useState(null);
  const [form, setForm] = useState({ title: '', content_type: 'text', content: '', content_refs: [], file_url: '', file_name: '', show_name: 'نعم', want_publish: 'نعم' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const fileRef = useRef(null);
  const editorRef = useRef(null);

  const DRAFT_KEY = editingProd ? `prod_draft_edit_${editingProd.id}` : 'prod_draft_new';

  useEffect(() => { loadProductions(); }, []);

  // Autosave form to localStorage every 1.5s while modal is open
  useEffect(() => {
    if (!modalOpen) return;
    const t = setTimeout(() => {
      try {
        const hasAnything = (form.title && form.title.trim()) || (form.content && form.content.trim()) || form.file_url || (form.content_refs || []).some(r => r && r.trim());
        if (hasAnything) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify({ form, savedAt: Date.now() }));
          setDraftSaved(true);
          setTimeout(() => setDraftSaved(false), 1200);
        }
      } catch {}
    }, 1500);
    return () => clearTimeout(t);
  }, [form, modalOpen, DRAFT_KEY]);

  // When opening modal for NEW production, try to restore draft
  const openNewModal = () => {
    try {
      const raw = localStorage.getItem('prod_draft_new');
      if (raw) {
        const { form: savedForm } = JSON.parse(raw);
        if (savedForm && (savedForm.title || savedForm.content)) {
          setForm(savedForm);
          toast.info('تم استرجاع مسودتك المحفوظة تلقائياً');
          setModalOpen(true);
          return;
        }
      }
    } catch {}
    setModalOpen(true);
  };

  const loadProductions = async () => {
    try { const res = await api.get('/productions'); setProductions(res.data); }
    catch { toast.error('فشل تحميل النتاج'); }
    finally { setLoading(false); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/upload/document', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((p) => ({ ...p, file_url: `${process.env.REACT_APP_BACKEND_URL}/api/files/${res.data.path}`, file_name: res.data.filename }));
      toast.success('تم رفع الملف');
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل الرفع'); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleSubmit = async () => {
    if (!form.title) { toast.error('يرجى كتابة العنوان'); return; }
    if (form.content_type === 'text' && !form.content) { toast.error('يرجى كتابة المحتوى'); return; }
    if (form.content_type === 'file' && !form.file_url) { toast.error('يرجى رفع الملف'); return; }
    setSaving(true);
    try {
      const payload = { ...form, content_refs: (form.content_refs || []).filter(r => r && r.trim()) };
      if (editingProd) {
        await api.put(`/productions/${editingProd.id}`, payload);
        toast.success('تم تحديث النتاج بنجاح');
      } else {
        await api.post('/productions', payload);
        toast.success('تم رفع النتاج بنجاح وسيتم مراجعته');
      }
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      closeModal();
      loadProductions();
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل العملية'); }
    finally { setSaving(false); }
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingProd(null);
    setForm({ title: '', content_type: 'text', content: '', content_refs: [], file_url: '', file_name: '', show_name: 'نعم', want_publish: 'نعم' });
  };

  const openEdit = (prod) => {
    setEditingProd(prod);
    // Try restore edit draft first
    try {
      const raw = localStorage.getItem(`prod_draft_edit_${prod.id}`);
      if (raw) {
        const { form: savedForm } = JSON.parse(raw);
        if (savedForm) {
          setForm(savedForm);
          setModalOpen(true);
          toast.info('تم استرجاع تعديلاتك المحفوظة تلقائياً');
          return;
        }
      }
    } catch {}
    setForm({ title: prod.title, content_type: prod.content_type, content: prod.content || '', content_refs: prod.content_refs || [], file_url: prod.file_url || '', file_name: prod.file_name || '', show_name: prod.show_name || 'نعم', want_publish: prod.want_publish || 'نعم' });
    setModalOpen(true);
  };

  // Reference management (Word-style: insert [N] at cursor + new row below)
  const insertReference = () => {
    const editor = editorRef.current?.editor;
    if (!editor) return;
    editor.chain().focus().insertContent('<sup>[?]</sup>').run();
    try { editor.view.dispatch(editor.state.tr.setStoredMarks([])); } catch {}
    const html = editor.getHTML();
    const regex = /<sup>\[([^\]]*)\]<\/sup>/g;
    const matches = [];
    let m;
    while ((m = regex.exec(html)) !== null) matches.push({ full: m[0], content: m[1], index: m.index });
    const newPos = matches.findIndex(x => x.content === '?');
    if (newPos === -1) return;
    let newHtml = '';
    let lastEnd = 0;
    matches.forEach((mm, i) => {
      newHtml += html.slice(lastEnd, mm.index);
      newHtml += `<sup>[${i + 1}]</sup>`;
      lastEnd = mm.index + mm.full.length;
    });
    newHtml += html.slice(lastEnd);
    const existingRefs = form.content_refs || [];
    const newRefs = matches.map(mm => {
      if (mm.content === '?') return '';
      const oldIdx = parseInt(mm.content, 10) - 1;
      if (isNaN(oldIdx) || oldIdx < 0) return '';
      return existingRefs[oldIdx] || '';
    });
    editor.commands.setContent(newHtml);
    try { editor.view.dispatch(editor.state.tr.setStoredMarks([])); } catch {}
    setForm(f => ({ ...f, content: newHtml, content_refs: newRefs }));
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="prod-ref-input-${newPos}"]`);
      if (el) el.focus();
    }, 150);
  };

  const updateProdRef = (i, val) => {
    setForm(f => ({ ...f, content_refs: (f.content_refs || []).map((r, idx) => idx === i ? val : r) }));
  };

  const removeProdRef = (i) => {
    const removedNum = i + 1;
    let html = form.content || '';
    html = html.replace(new RegExp(`<sup>\\[${removedNum}\\]</sup>`, 'g'), '');
    const matches = [];
    const re = /<sup>\[([^\]]*)\]<\/sup>/g;
    let m;
    while ((m = re.exec(html)) !== null) matches.push({ full: m[0], index: m.index });
    let newHtml = '';
    let lastEnd = 0;
    matches.forEach((mm, idx) => {
      newHtml += html.slice(lastEnd, mm.index);
      newHtml += `<sup>[${idx + 1}]</sup>`;
      lastEnd = mm.index + mm.full.length;
    });
    newHtml += html.slice(lastEnd);
    if (editorRef.current?.editor) editorRef.current.editor.commands.setContent(newHtml);
    setForm(f => ({ ...f, content: newHtml, content_refs: (f.content_refs || []).filter((_, idx) => idx !== i) }));
  };

  const handleDelete = async (prod) => {
    if (!window.confirm(`هل تريد حذف "${prod.title}"؟`)) return;
    setDeleting(prod.id);
    try {
      await api.delete(`/productions/${prod.id}`);
      toast.success('تم حذف النتاج');
      loadProductions();
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل الحذف'); }
    finally { setDeleting(null); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div></div>;

  return (
    <div className="space-y-8" data-testid="my-productions-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-4xl font-bold text-accent">نتاجي</h1>
          <p className="text-muted-foreground text-lg mt-1">شارك نتاجك العلمي ليراجعه المشرفون</p>
        </div>
        <Button onClick={openNewModal} className="bg-primary hover:bg-primary/90" data-testid="new-production-btn">
          <Plus className="w-5 h-5 ml-2" /> رفع نتاج جديد
        </Button>
      </div>

      {productions.length === 0 ? (
        <div className="text-center py-16">
          <PenLine className="w-16 h-16 text-muted mx-auto mb-4 opacity-30" />
          <p className="text-lg text-muted-foreground">لم ترفع أي نتاج بعد</p>
          <p className="text-sm text-muted-foreground mt-1">اضغط على "رفع نتاج جديد" لتبدأ</p>
        </div>
      ) : (
        <div className="space-y-4">
          {productions.map((prod) => {
            const st = STATUS_MAP[prod.status] || STATUS_MAP.pending;
            const Icon = st.icon;
            return (
              <Card key={prod.id} className="p-5 hover:shadow-md transition-shadow" data-testid={`production-${prod.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl border ${st.color} shrink-0`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-bold text-lg text-accent truncate">{prod.title}</h3>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border ${st.color} shrink-0`}>{st.label}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span>{prod.content_type === 'file' ? 'ملف مرفق' : 'نص مكتوب'}</span>
                      <span>{new Date(prod.created_at).toLocaleDateString('ar')}</span>
                    </div>
                    {prod.admin_notes && (
                      <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg text-sm">
                        <p className="font-semibold text-orange-800 mb-1">ملاحظات اللجنة العلمية:</p>
                        <div
                          className="answer-html text-orange-900"
                          dir="rtl"
                          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(prod.admin_notes) }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {EDITABLE.includes(prod.status) && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => openEdit(prod)} data-testid={`edit-prod-${prod.id}`}>
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(prod)} disabled={deleting === prod.id}
                          className="text-red-500 border-red-200 hover:bg-red-50" data-testid={`delete-prod-${prod.id}`}>
                          {deleting === prod.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                        </Button>
                      </>
                    )}
                    <Button variant="outline" size="sm" onClick={() => setViewProd(prod)} data-testid={`view-prod-${prod.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-accent">{editingProd ? 'تعديل النتاج' : 'رفع نتاج جديد'}</h2>
              <button onClick={closeModal}><X className="w-6 h-6 text-muted-foreground" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">العنوان *</label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="عنوان النتاج..." data-testid="prod-title" />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">نوع المحتوى</label>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setForm({ ...form, content_type: 'text' })}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold transition-all ${form.content_type === 'text' ? 'border-primary bg-primary/10 text-primary' : 'border-gray-200 text-gray-500'}`}
                    data-testid="prod-type-text">
                    <PenLine className="w-5 h-5" /> كتابة نص
                  </button>
                  <button type="button" onClick={() => setForm({ ...form, content_type: 'file' })}
                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border-2 font-semibold transition-all ${form.content_type === 'file' ? 'border-red-400 bg-red-50 text-red-600' : 'border-gray-200 text-gray-500'}`}
                    data-testid="prod-type-file">
                    <FileText className="w-5 h-5" /> رفع ملف أو صورة
                  </button>
                </div>
              </div>

              {form.content_type === 'text' ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="block text-sm font-medium">المحتوى</label>
                    <span className={`text-[11px] transition-opacity ${draftSaved ? 'text-emerald-600 opacity-100' : 'text-gray-400 opacity-70'}`} data-testid="prod-autosave">
                      {draftSaved ? '✓ تم الحفظ التلقائي' : 'حفظ تلقائي كل ثانيتين'}
                    </span>
                  </div>
                  <RichTextEditor
                    value={form.content}
                    onChange={(val) => setForm(f => ({ ...f, content: val }))}
                    onEditorReady={(ed) => { editorRef.current = { editor: ed }; }}
                    extraToolbar={(_ed, MenuButton) => (
                      <MenuButton onClick={insertReference} title="إضافة مرجع/مصدر">
                        <BookMarked size={16} />
                      </MenuButton>
                    )}
                  />

                  {/* References Panel */}
                  <div className="border-2 border-amber-200 rounded-xl bg-amber-50/40 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-bold text-amber-900 flex items-center gap-2">
                        <BookMarked className="w-4 h-4" /> المراجع والمصادر ({(form.content_refs || []).length})
                      </h4>
                      <Button size="sm" type="button" variant="outline" onClick={insertReference} className="text-amber-700 border-amber-300 hover:bg-amber-100" data-testid="prod-add-ref-btn">
                        <Plus className="w-3 h-3 ml-1" /> إضافة مرجع
                      </Button>
                    </div>
                    {(form.content_refs || []).length === 0 ? (
                      <p className="text-xs text-gray-500 py-2 text-center">اضغط "إضافة مرجع" — سيُدرج رقم في موضع المؤشر، ويظهر صف هنا للكتابة.</p>
                    ) : (
                      <ol className="space-y-1.5 list-none p-0">
                        {(form.content_refs || []).map((r, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <span className="shrink-0 w-7 h-7 rounded-full bg-amber-200 text-amber-900 flex items-center justify-center font-bold text-xs">{i + 1}</span>
                            <input
                              value={r}
                              onChange={e => updateProdRef(i, e.target.value)}
                              placeholder="اكتب المصدر هنا..."
                              className="flex-1 border border-amber-200 rounded-lg px-2 py-1.5 text-sm bg-white focus:outline-none focus:border-amber-400"
                              dir="rtl"
                              data-testid={`prod-ref-input-${i}`}
                            />
                            <button type="button" onClick={() => removeProdRef(i)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" title="حذف المرجع">
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              ) : (
                <div>
                  {form.file_url ? (
                    <div className="border-2 border-green-200 bg-green-50 rounded-xl p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <FileText className="w-8 h-8 text-green-600" />
                        <div><p className="font-semibold text-green-800">تم رفع الملف</p><p className="text-sm text-green-600 truncate max-w-[300px]">{form.file_name}</p></div>
                      </div>
                      <Button type="button" variant="outline" size="sm" onClick={() => setForm({ ...form, file_url: '', file_name: '' })} className="text-red-500 border-red-200">
                        <X className="w-4 h-4 ml-1" /> إزالة
                      </Button>
                    </div>
                  ) : (
                    <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed rounded-xl p-8 text-center cursor-pointer hover:border-primary/50 transition-colors">
                      {uploading ? <Loader2 className="w-10 h-10 animate-spin mx-auto text-primary" /> : (
                        <><Upload className="w-10 h-10 mx-auto text-gray-400 mb-3" /><p className="font-semibold text-gray-600">اضغط لرفع ملف PDF أو Word أو صورة</p><p className="text-sm text-gray-400 mt-1">الحد الأقصى: 20 ميجابايت</p></>
                      )}
                    </div>
                  )}
                  <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif,.webp" className="hidden" onChange={handleFileUpload} />
                </div>
              )}
            </div>

            {/* Want publish toggle */}
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <label className="block text-sm font-semibold text-blue-900 mb-2">هل تريد نشر نتاجك؟</label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setForm({ ...form, want_publish: 'نعم' })}
                  className={`flex-1 py-2.5 rounded-lg border-2 font-semibold text-sm transition-all ${form.want_publish === 'نعم' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500'}`}
                  data-testid="want-publish-yes">
                  نعم
                </button>
                <button type="button" onClick={() => setForm({ ...form, want_publish: 'لا' })}
                  className={`flex-1 py-2.5 rounded-lg border-2 font-semibold text-sm transition-all ${form.want_publish === 'لا' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500'}`}
                  data-testid="want-publish-no">
                  لا
                </button>
              </div>
            </div>

            {/* Show name toggle */}
            <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <label className="block text-sm font-semibold text-amber-900 mb-2">إظهار اسمك عند نشر المبادرة لنتاجك:</label>
              <div className="flex gap-3">
                <button type="button" onClick={() => setForm({ ...form, show_name: 'نعم' })}
                  className={`flex-1 py-2.5 rounded-lg border-2 font-semibold text-sm transition-all ${form.show_name === 'نعم' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 text-gray-500'}`}
                  data-testid="show-name-yes">
                  نعم
                </button>
                <button type="button" onClick={() => setForm({ ...form, show_name: 'لا' })}
                  className={`flex-1 py-2.5 rounded-lg border-2 font-semibold text-sm transition-all ${form.show_name === 'لا' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-500'}`}
                  data-testid="show-name-no">
                  لا
                </button>
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={handleSubmit} disabled={saving} className="flex-1 bg-primary hover:bg-primary/90" data-testid="submit-prod-btn">
                {saving ? 'جارٍ الحفظ...' : editingProd ? 'تحديث النتاج' : 'رفع النتاج'}
              </Button>
              <Button variant="outline" onClick={closeModal} className="flex-1">إلغاء</Button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {viewProd && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-accent">{viewProd.title}</h2>
              <button onClick={() => setViewProd(null)}><X className="w-6 h-6 text-muted-foreground" /></button>
            </div>
            {viewProd.content_type === 'file' && viewProd.file_url ? (
              <iframe src={`${viewProd.file_url}#toolbar=1`} title={viewProd.title} className="w-full border-0 rounded-lg" style={{ height: '70vh' }} />
            ) : (
              <>
                <div className="prose prose-lg max-w-none answer-html" dir="rtl" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(viewProd.content || '<p>لا يوجد محتوى</p>') }} />
                {Array.isArray(viewProd.content_refs) && viewProd.content_refs.length > 0 && (
                  <div className="mt-6 pt-4 border-t-2 border-amber-200">
                    <h4 className="text-sm font-bold text-amber-900 mb-2 flex items-center gap-2"><BookMarked className="w-4 h-4" /> المراجع والمصادر</h4>
                    <ol className="text-sm text-amber-900 list-decimal pr-6 space-y-1" dir="rtl">
                      {viewProd.content_refs.map((r, i) => <li key={i}>{r}</li>)}
                    </ol>
                  </div>
                )}
              </>
            )}

            {/* Admin thread — always available on the reader-side view so the
                reader can read the committee's notes and reply with files. */}
            <div className="mt-6 pt-4 border-t-2 border-gray-100">
              <ProductionThread
                productionId={viewProd.id}
                viewerType="reader"
                adminNotes={viewProd.admin_notes}
                adminAttachmentUrl={viewProd.admin_attachment_url}
                adminAttachmentName={viewProd.admin_attachment_name}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyProductionsPage;
